import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, X, Loader2 } from 'lucide-react';
import { useBlogPosts } from '../hooks/useBlogPosts';

function useFadeIn() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function FadeSection({ children, className = '' }) {
  const [ref, visible] = useFadeIn();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
    >
      {children}
    </div>
  );
}

function ArticleModal({ post, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!post) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="blog-article-title"
    >
      <button
        type="button"
        aria-label="Close article"
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />
      <article className="relative max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-2xl bg-white sm:rounded-2xl">
        <div className="relative aspect-[16/9] overflow-hidden bg-[#fdf8f6] sm:aspect-[21/9]">
          <img src={post.image} alt="" className="h-full w-full object-cover" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-4 rounded-full bg-black/70 p-2 text-white transition-colors hover:bg-black"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 sm:p-10">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-[#fdf1ec] px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#cc7c66]">
              {post.category}
            </span>
            <span className="text-[11px] text-gray-400">{post.date}</span>
            <span className="flex items-center gap-1 text-[11px] text-gray-400">
              <Clock size={12} /> {post.readTime}
            </span>
          </div>

          <h2 id="blog-article-title" className="mb-4 font-display text-3xl font-light leading-tight sm:text-4xl">
            {post.title}
          </h2>

          <p className="mb-6 text-base font-light leading-relaxed text-gray-600">{post.excerpt}</p>

          <div className="space-y-4 border-t border-gray-100 pt-6">
            {post.body.map((paragraph) => (
              <p key={paragraph.slice(0, 24)} className="text-sm font-light leading-relaxed text-gray-600">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-4 border-t border-gray-100 pt-8">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 border border-black px-6 py-3 text-xs uppercase tracking-[0.2em] transition-colors hover:bg-black hover:text-white"
            >
              Shop Actives <ArrowRight size={14} />
            </Link>
            <Link
              to="/ingredients"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gray-500 transition-colors hover:text-black"
            >
              Ingredient Library <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}

function BlogCard({ post, onRead, large = false }) {
  return (
    <article
      className={`group flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white transition-colors hover:border-black ${
        large ? 'lg:flex-row' : ''
      }`}
    >
      <button
        type="button"
        onClick={() => onRead(post)}
        className={`overflow-hidden bg-[#fdf8f6] text-left ${large ? 'lg:w-1/2' : 'w-full'}`}
      >
        <div className={`overflow-hidden ${large ? 'h-full min-h-[220px] lg:min-h-[320px]' : 'aspect-[4/3]'}`}>
          <img
            src={post.image}
            alt=""
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
      </button>

      <div className={`flex flex-1 flex-col p-6 sm:p-8 ${large ? 'lg:justify-center' : ''}`}>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-[#fdf1ec] px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#cc7c66]">
            {post.category}
          </span>
          <span className="text-[11px] text-gray-400">{post.date}</span>
        </div>

        <h3
          className={`mb-3 font-display font-light leading-tight text-black ${
            large ? 'text-3xl sm:text-4xl' : 'text-2xl'
          }`}
        >
          {post.title}
        </h3>

        <p className="mb-5 flex-1 text-sm font-light leading-relaxed text-gray-600">{post.snippet}</p>

        <button
          type="button"
          onClick={() => onRead(post)}
          className="inline-flex w-fit items-center gap-1.5 border-b border-black pb-0.5 text-[11px] uppercase tracking-[0.2em] transition-colors hover:text-gray-500"
        >
          Read Article <ArrowRight size={12} />
        </button>
      </div>
    </article>
  );
}

export default function Blog() {
  const { posts: blogPosts, categories: blogCategories, loading } = useBlogPosts();
  const [activeCategory, setActiveCategory] = useState('All');
  const [activePost, setActivePost] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update active category when categories load
  useEffect(() => {
    if (blogCategories.length > 1 && !blogCategories.includes(activeCategory)) {
      setActiveCategory('All');
    }
  }, [blogCategories, activeCategory]);

  const featured = blogPosts.find((p) => p.featured) ?? blogPosts[0];
  const showFeatured = featured && (activeCategory === 'All' || featured.category === activeCategory);

  const gridPosts = featured
    ? activeCategory === 'All'
      ? blogPosts.filter((p) => p.id !== featured.id)
      : blogPosts.filter((p) => p.category === activeCategory)
    : blogPosts;

  return (
    <main className="min-h-screen">
      <section className="relative overflow-hidden bg-black text-white">
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 75% 30%, #cc7c66 0%, transparent 55%)' }}
        />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-28">
          <p className="mb-5 text-xs uppercase tracking-[0.35em] text-[#cc7c66]">Metameni Journal</p>
          <h1 className="max-w-3xl font-display text-5xl font-light leading-tight sm:text-6xl lg:text-7xl">
            Learn the science behind
            <br />
            <em className="not-italic text-[#f5e6e0]">better skin.</em>
          </h1>
          <p className="mt-6 max-w-xl text-sm font-light leading-relaxed text-gray-300 sm:text-base">
            Evidence-based guides on routines, actives, and Indian skin concerns — written by our
            team so you can build a routine that actually works.
          </p>
        </div>
      </section>

      <section className="sticky top-0 z-30 border-b border-gray-100 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-7xl gap-2 overflow-x-auto px-6 py-4 lg:px-8">
          {blogCategories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActiveCategory(cat)}
              className={`flex-shrink-0 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.18em] transition-colors ${
                activeCategory === cat
                  ? 'bg-black text-white'
                  : 'border border-gray-200 text-gray-600 hover:border-black hover:text-black'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <FadeSection>
            <p className="mb-2 text-xs uppercase tracking-[0.3em] text-gray-400">
              {activeCategory === 'All' ? 'Latest' : activeCategory}
            </p>
            <h2 className="mb-10 font-display text-4xl font-light sm:text-5xl">
              {activeCategory === 'All' ? 'All articles' : `${activeCategory} guides`}
            </h2>
          </FadeSection>

          {showFeatured && activeCategory === 'All' && (
            <FadeSection className="mb-10">
              <p className="mb-4 text-[11px] uppercase tracking-[0.25em] text-[#cc7c66]">Featured</p>
              <BlogCard post={featured} onRead={setActivePost} large />
            </FadeSection>
          )}

          <FadeSection>
            {gridPosts.length > 0 ? (
              <div
                className={`grid gap-6 ${
                  gridPosts.length === 1
                    ? 'max-w-2xl grid-cols-1'
                    : activeCategory === 'All' && showFeatured
                      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                      : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                }`}
              >
                {gridPosts.map((post) => (
                  <BlogCard key={post.id} post={post} onRead={setActivePost} />
                ))}
              </div>
            ) : (
              <p className="py-16 text-center text-sm font-light text-gray-500">
                No articles in this category yet. Check back soon.
              </p>
            )}
          </FadeSection>
        </div>
      </section>

      <section className="bg-[#fdf8f6] py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="rounded-2xl border border-gray-200 bg-white p-8 sm:p-10">
            <p className="mb-3 text-xs uppercase tracking-[0.3em] text-gray-400">What we cover</p>
            <h2 className="mb-8 font-display text-3xl font-light sm:text-4xl">Skincare, explained clearly</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { label: 'Routine building', desc: 'AM/PM order, layering, and minimalist schedules' },
                { label: 'Active ingredients', desc: 'What works, at what dose, and how to combine' },
                { label: 'Indian skin concerns', desc: 'Pigmentation, acne, oil control, and climate' },
                { label: 'Barrier & science', desc: 'Repair, sensitivity, and long-term skin health' },
              ].map((topic) => (
                <div key={topic.label} className="border border-gray-200 p-5">
                  <h3 className="mb-2 text-sm font-medium">{topic.label}</h3>
                  <p className="text-xs font-light leading-relaxed text-gray-500">{topic.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-black py-20 text-white">
        <div className="mx-auto max-w-5xl px-6 text-center lg:px-8">
          <p className="mb-3 text-xs uppercase tracking-[0.3em] text-[#cc7c66]">Put it into practice</p>
          <h2 className="mb-6 font-display text-4xl font-light sm:text-5xl">
            Ready to build your routine?
          </h2>
          <p className="mx-auto mb-9 max-w-2xl text-sm font-light leading-relaxed text-gray-300">
            Explore formulas matched to your skin concern — backed by the same science you read
            about here.
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 border border-white/40 px-8 py-4 text-xs uppercase tracking-[0.25em] transition-colors hover:bg-white hover:text-black"
          >
            Shop Products <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {activePost && <ArticleModal post={activePost} onClose={() => setActivePost(null)} />}
    </main>
  );
}
